import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";

/**
 * Full-screen image lightbox with zoom, drag-to-pan, multi-image navigation.
 *
 * Props:
 *   images  – array of { src, caption? }  OR
 *   src     – single image URL (shorthand)
 *   index   – initial image index (default 0)
 *   onClose – callback
 */
export default function Lightbox({ src, images, index: initialIndex = 0, onClose }) {
  const imgs = src ? [{ src }] : (images || []);
  const [idx, setIdx]     = useState(initialIndex);
  const [zoom, setZoom]   = useState(1);
  const [pan, setPan]     = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragOrigin = useRef(null);
  const panAtDragStart = useRef({ x: 0, y: 0 });
  const imgRef = useRef(null);

  const cur = imgs[idx] || {};

  const close = useCallback(() => { setZoom(1); setPan({ x:0,y:0 }); onClose(); }, [onClose]);

  // Navigation helpers
  const prev = useCallback(() => { setZoom(1); setPan({x:0,y:0}); setIdx(i => (i - 1 + imgs.length) % imgs.length); }, [imgs.length]);
  const next = useCallback(() => { setZoom(1); setPan({x:0,y:0}); setIdx(i => (i + 1) % imgs.length); }, [imgs.length]);

  // Keyboard: ESC, arrows
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape")      close();
      if (e.key === "ArrowLeft")   prev();
      if (e.key === "ArrowRight")  next();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [close, prev, next]);

  // Prevent body scroll while open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  // Scroll to zoom
  const onWheel = (e) => {
    e.preventDefault();
    const delta = -e.deltaY * 0.001;
    setZoom(z => Math.min(8, Math.max(1, z + delta * z)));
    if (zoom <= 1.05) setPan({ x: 0, y: 0 });
  };

  // Double-click: toggle zoom
  const onDblClick = () => {
    if (zoom > 1) { setZoom(1); setPan({ x: 0, y: 0 }); }
    else setZoom(2.5);
  };

  // Drag-to-pan
  const onMouseDown = (e) => {
    if (zoom <= 1) return;
    e.preventDefault();
    setDragging(true);
    dragOrigin.current = { x: e.clientX, y: e.clientY };
    panAtDragStart.current = { ...pan };
  };
  const onMouseMove = (e) => {
    if (!dragging) return;
    setPan({
      x: panAtDragStart.current.x + (e.clientX - dragOrigin.current.x),
      y: panAtDragStart.current.y + (e.clientY - dragOrigin.current.y),
    });
  };
  const onMouseUp   = () => setDragging(false);
  const onMouseLeave = () => setDragging(false);

  // Touch zoom (pinch)
  const lastTouchDist = useRef(null);
  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      lastTouchDist.current = Math.sqrt(dx*dx + dy*dy);
    }
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && lastTouchDist.current) {
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.sqrt(dx*dx + dy*dy);
      const ratio = dist / lastTouchDist.current;
      setZoom(z => Math.min(8, Math.max(1, z * ratio)));
      lastTouchDist.current = dist;
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] select-none"
      style={{ background: "rgba(0,0,0,0.97)", backdropFilter: "blur(4px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      {/* ── Top bar ── */}
      <div className="absolute top-0 inset-x-0 z-10 flex items-center justify-between px-4 py-3"
        style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0.8), transparent)" }}>
        {/* Counter */}
        {imgs.length > 1 && (
          <span className="text-sm font-semibold text-white/60 tabular-nums">
            {idx + 1} / {imgs.length}
          </span>
        )}
        <div className="flex-1" />
        {/* Zoom indicator */}
        {zoom > 1 && (
          <span className="text-xs text-white/40 mr-3 tabular-nums">{Math.round(zoom * 100)}%</span>
        )}
        {/* Download */}
        <a
          href={cur.src}
          download
          target="_blank"
          rel="noopener noreferrer"
          className="w-10 h-10 rounded-2xl flex items-center justify-center mr-2 transition-all hover:bg-white/10"
          style={{ color: "rgba(255,255,255,0.6)" }}
          onClick={e => e.stopPropagation()}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
        </a>
        {/* Close */}
        <button
          onClick={close}
          className="w-10 h-10 rounded-2xl flex items-center justify-center transition-all hover:bg-white/10 active:scale-95"
          style={{ color: "rgba(255,255,255,0.7)" }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>
      </div>

      {/* ── Prev/Next arrows ── */}
      {imgs.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-2xl flex items-center justify-center transition-all hover:bg-white/10 active:scale-95"
            style={{ color: "rgba(255,255,255,0.7)" }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M15 18l-6-6 6-6"/></svg>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-2xl flex items-center justify-center transition-all hover:bg-white/10 active:scale-95"
            style={{ color: "rgba(255,255,255,0.7)" }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M9 18l6-6-6-6"/></svg>
          </button>
        </>
      )}

      {/* ── Image ── */}
      <div
        className="absolute inset-0 flex items-center justify-center overflow-hidden"
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
      >
        <img
          ref={imgRef}
          key={cur.src}
          src={cur.src}
          alt=""
          draggable={false}
          onWheel={onWheel}
          onDoubleClick={onDblClick}
          onMouseDown={onMouseDown}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onClick={e => e.stopPropagation()}
          style={{
            maxWidth: "100vw",
            maxHeight: "100vh",
            width: "auto",
            height: "auto",
            objectFit: "contain",
            transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
            transformOrigin: "center center",
            cursor: zoom > 1 ? (dragging ? "grabbing" : "grab") : "zoom-in",
            transition: dragging ? "none" : "transform 0.15s ease",
            userSelect: "none",
            WebkitUserDrag: "none",
          }}
        />
      </div>

      {/* ── Caption ── */}
      {cur.caption && (
        <div className="absolute bottom-0 inset-x-0 flex justify-center pb-6 pointer-events-none">
          <span className="px-4 py-2 rounded-2xl text-sm text-white/70 font-medium"
            style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(8px)" }}>
            {cur.caption}
          </span>
        </div>
      )}

      {/* ── Zoom hint (shown briefly) ── */}
      {zoom === 1 && (
        <div className="absolute bottom-4 inset-x-0 flex justify-center pointer-events-none">
          <span className="text-xs text-white/20 font-medium">
            Scroll to zoom · Double-click to zoom · ESC to close
          </span>
        </div>
      )}
    </div>,
    document.body
  );
}
